import { useCallback, useEffect, useState } from "react";
import NavigationTabs from "../Models/MicroSlide/NavigationTabs";
import { Box, Chip, CircularProgress, Tooltip, IconButton, Snackbar, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_BASE_URL } from "../../utils/constants";

// Pings GET /health on mount (and every 4 minutes thereafter) to keep the
// AWS Lambda warm. Container-image cold starts can run 5-15s; the chip in
// the page header surfaces the warmup state so the user knows when calls
// will return quickly.
const HEALTH_URL = (API_BASE_URL || "").replace(/\/+$/, "") + "/health";
const RE_WARM_INTERVAL_MS = 4 * 60 * 1000;
const WARMUP_TIMEOUT_MS = 20 * 1000;

const MicroSlide = () => {
    // status: "warming" | "ready" | "error"
    const [status, setStatus] = useState("warming");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [lastReadyAt, setLastReadyAt] = useState(null);

    const warmupBackend = useCallback(async (showSnackbar = false) => {
        setStatus("warming");
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), WARMUP_TIMEOUT_MS);
        try {
            // Any HTTP response (including 404) proves the Lambda is awake.
            // The deployed route table may or may not include /health
            // depending on how the Lambda Web Adapter forwards paths.
            await fetch(HEALTH_URL, { method: "GET", signal: controller.signal });
            setStatus("ready");
            setLastReadyAt(new Date());
            if (showSnackbar) setSnackbarOpen(true);
        } catch (err) {
            // Treat anything that is not a true network failure as "ready";
            // the Lambda answered, the browser just refused to read the body
            // (e.g. CORS rejection on an upstream 4xx without CORS headers).
            const msg = String(err?.message || "");
            const reallyOffline = err?.name === "AbortError"
                || /Failed to fetch|NetworkError|ECONNREFUSED/i.test(msg);
            if (reallyOffline) {
                setStatus("error");
            } else {
                setStatus("ready");
                setLastReadyAt(new Date());
                if (showSnackbar) setSnackbarOpen(true);
            }
        } finally {
            clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        warmupBackend(true);
        const interval = setInterval(() => warmupBackend(false), RE_WARM_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [warmupBackend]);

    const statusChip = (() => {
        if (status === "warming") {
            return (
                <Tooltip title="Pinging /health to wake the AWS Lambda. Container-image Lambdas can take 5-15s to cold-start.">
                    <Chip
                        size="small"
                        color="warning"
                        icon={<CircularProgress size={14} sx={{ color: "inherit" }} />}
                        label="Warming backend..."
                    />
                </Tooltip>
            );
        }
        if (status === "ready") {
            const stamp = lastReadyAt ? ` (${lastReadyAt.toLocaleTimeString()})` : "";
            return (
                <Tooltip title={`Lambda responded to /health${stamp}. Calls should return in ~200 ms. Auto re-warms every 4 minutes.`}>
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon fontSize="small" />}
                        label={`Backend ready${stamp}`}
                    />
                </Tooltip>
            );
        }
        return (
            <Tooltip title="Lambda did not respond to /health within 20s. Calls will still retry, but the first one may take a while. Click the refresh icon to try again.">
                <Chip
                    size="small"
                    color="error"
                    icon={<ErrorIcon fontSize="small" />}
                    label="Backend offline / cold"
                />
            </Tooltip>
        );
    })();

    return (
        <Box sx={{ p: 0 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 1,
                    px: 2,
                    pt: 1
                }}
            >
                {statusChip}
                <Tooltip title="Re-ping /health">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => warmupBackend(true)}
                            disabled={status === "warming"}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <NavigationTabs />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => setSnackbarOpen(false)}
                    sx={{ width: "100%" }}
                >
                    Backend ready - safe to run Response / Calibration / Prediction
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MicroSlide;
